import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { GTDialogBody, GTDialogFooter } from '@/components/ui/gt-dialog'
import { cn } from '@/lib/utils'
import { AlertTriangle, CalendarRange, Flag, Gauge, Plus, Sparkles } from 'lucide-react'
import { useRecurringTaskTemplates } from '../../../../services/api/recurring-tasks.hooks'
import { GoalHookCheckboxList, activeRecurringTemplates, toggleHookSelection } from '../../RecurringGoalHooks'
import { TGoalType } from '../../goalTypes'
import PlanFeedback, { PlanFeedbackInvite, focusPlanFeedback } from './PlanFeedback'
import PropertyPill, { CAPACITY_OPTIONS, CAPACITY_UNITS, TIMEFRAME_OPTIONS, capacityFromLabel } from './PropertyPill'
import { TDraftItemKind, TDraftPlanItem, TGoalDraft, weeklyTargetForCadences } from './scenario'
import { planningCommitmentReview } from './startingPoint'

interface ReviewScreenProps {
    draft: TGoalDraft
    detectedNote?: string
    /** Optional source note shown below the goal properties. */
    provenanceNote?: string
    /** Manual fallback only: show the explicit Consistency / Time-based toggle (AI paths carry the type). */
    showTypePicker?: boolean
    onConfirm: (draft: TGoalDraft) => void
    /**
     * Ask the planner for a revision. Gets the note plus the same draft `onConfirm` would
     * send, so the revision is based on the screen as edited — including a timeframe or
     * capacity changed here, which is what shapes the plan. Omit for the hand-built and lab
     * flows, which have no planner behind them — the feedback box stays hidden.
     */
    onRevise?: (feedback: string, draft: TGoalDraft) => void
    isRevising?: boolean
    onBack?: () => void
    /**
     * `panel` (default): self-contained scroll region + bordered footer, for CreationModal / OnboardingFrame.
     * `dialog`: renders straight into GTDialog's body + footer slots (the scaffold wizard).
     */
    layout?: 'panel' | 'dialog'
}

const TYPE_OPTIONS: { value: TGoalType; label: string }[] = [
    { value: 'consistency', label: 'Consistency' },
    { value: 'time', label: 'Time-based' },
]

/** Per-cadence frequency options — local because only the plan rows use them. */
const FREQUENCY_OPTIONS = ['1× / week', '2× / week', '3× / week', '1× / month']

/**
 * The plan vocabulary, owned here: section heading, add-row copy, and the label the
 * per-row kind picker shows. The picker's options are derived from this list so there is
 * never a second list of kinds to keep in step.
 */
export const REVIEW_PLAN_GROUPS: { label: string; addLabel: string; itemLabel: string; kind: TDraftItemKind }[] = [
    { label: 'Cadences', addLabel: 'Add a cadence', itemLabel: 'Cadence', kind: 'cadence' },
    { label: 'One-offs', addLabel: 'Add a one-off', itemLabel: 'One-off', kind: 'oneoff' },
    { label: 'Milestones', addLabel: 'Add a milestone', itemLabel: 'Milestone', kind: 'milestone' },
]
const KIND_OPTIONS = REVIEW_PLAN_GROUPS.map((g) => g.itemLabel)
const kindForItemLabel = (itemLabel: string) => REVIEW_PLAN_GROUPS.find((g) => g.itemLabel === itemLabel)?.kind
const itemLabelForKind = (kind: TDraftItemKind) => REVIEW_PLAN_GROUPS.find((g) => g.kind === kind)?.itemLabel ?? kind
const DEFAULT_FREQUENCY_LABEL = FREQUENCY_OPTIONS[0]

let customItemCounter = 0

const ReviewScreen = ({
    draft,
    detectedNote,
    provenanceNote,
    showTypePicker,
    onConfirm,
    onRevise,
    isRevising,
    onBack,
    layout = 'panel',
}: ReviewScreenProps) => {
    const [title, setTitle] = useState(draft.title)
    const [timeframeLabel, setTimeframeLabel] = useState(draft.timeframeLabel)
    const [capacityLabel, setCapacityLabel] = useState(draft.capacityLabel)
    const [goalType, setGoalType] = useState<TGoalType>(draft.goalType ?? 'consistency')
    const [items, setItems] = useState<TDraftPlanItem[]>(draft.items)
    const { data: recurringTemplates = [] } = useRecurringTaskTemplates()
    const [selectedRecurringTemplateIds, setSelectedRecurringTemplateIds] = useState<string[]>(
        draft.recurringTemplateIds ?? []
    )
    // A revision replaces these rows when it lands, so creating the goal mid-flight would
    // save the plan the feedback was about. Blocks both footer buttons and the ⌘Enter path.
    const canConfirm = title.trim().length > 0 && !isRevising
    const feedbackRef = useRef<HTMLTextAreaElement>(null)
    const hookableTemplates = useMemo(() => activeRecurringTemplates(recurringTemplates), [recurringTemplates])

    // A sessions capacity is a ceiling the plan rows can still be pushed over by hand right here, and
    // the saved weekly target holds at the ceiling when they are. Say so instead of capping silently.
    const overCapacityNote = useMemo(() => {
        const stated = capacityFromLabel(capacityLabel)
        if (stated?.unit !== 'sessions') return undefined
        const asked = weeklyTargetForCadences(items.filter((it) => it.included && it.kind === 'cadence'))
        if (!asked || asked <= stated.value) return undefined
        return `Plan asks ${asked}× / week, over your ${capacityLabel} capacity.`
    }, [capacityLabel, items])

    const toggleItem = (id: string) =>
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, included: !it.included } : it)))

    const setFrequency = (id: string, frequencyLabel: string) =>
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, frequencyLabel } : it)))

    // Reclassifying a row keeps the title the planner (or the user) wrote and moves it to the
    // group that matches. Only cadences carry a frequency, so it is granted and revoked here.
    const setItemKind = (id: string, itemLabel: string) => {
        const kind = kindForItemLabel(itemLabel)
        if (!kind) return
        setItems((prev) =>
            prev.map((it) =>
                it.id === id
                    ? {
                          ...it,
                          kind,
                          frequencyLabel: kind === 'cadence' ? it.frequencyLabel ?? DEFAULT_FREQUENCY_LABEL : undefined,
                      }
                    : it
            )
        )
    }

    const setItemTitle = (id: string, title: string) =>
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, title } : it)))

    const addItem = (kind: TDraftPlanItem['kind']) =>
        setItems((prev) => [
            ...prev,
            {
                id: `custom-${++customItemCounter}`,
                kind,
                title: '',
                frequencyLabel: kind === 'cadence' ? DEFAULT_FREQUENCY_LABEL : undefined,
                included: true,
            },
        ])

    const toggleRecurringTemplate = (templateId: string) =>
        setSelectedRecurringTemplateIds((current) => toggleHookSelection(current, templateId))

    /** The screen as it stands. Both Create goal and Revise plan read it, so neither can
     *  act on a value the other would have sent. Rows left with an empty title are dropped. */
    const currentDraft = (): TGoalDraft => ({
        ...draft,
        title: title.trim(),
        timeframeLabel,
        capacityLabel,
        // Only the manual picker overrides the type — AI drafts carry it in.
        ...(showTypePicker ? { goalType } : {}),
        items: items.filter((it) => it.title.trim() !== ''),
        recurringTemplateIds: selectedRecurringTemplateIds,
    })

    // Keep the latest confirm payload in a ref so the ⌘Enter listener stays stable.
    const confirmRef = useRef<() => void>()
    confirmRef.current = () => {
        if (!canConfirm) return
        onConfirm(currentDraft())
    }
    const handleConfirm = () => confirmRef.current?.()

    // ⌘Enter (or Ctrl+Enter) anywhere in the modal confirms. This listener is
    // active only while ReviewScreen is mounted — i.e. while the modal is open.
    useEffect(() => {
        const onKey = (e: globalThis.KeyboardEvent) => {
            if (!(e.metaKey || e.ctrlKey) || e.key !== 'Enter') return
            // Inside the feedback box ⌘Enter means "revise", not "create the goal".
            if ((e.target as HTMLElement | null)?.closest?.('[data-plan-feedback]')) return
            e.preventDefault()
            confirmRef.current?.()
        }
        // Capture phase: GTDialog stops bubbling at the React root, so a bubbling listener would never fire.
        window.addEventListener('keydown', onKey, true)
        return () => window.removeEventListener('keydown', onKey, true)
    }, [])

    // Continuous index so row enter animations stagger across all groups.
    let rowIndex = 0

    const body = (
        <>
            {/* Title + property row */}
            <div className="space-y-2.5">
                <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Goal title"
                    aria-label="Goal title"
                    className="w-full border-0 bg-transparent p-0 text-title-sm touch-field-floor text-foreground placeholder:text-muted-foreground focus-visible:outline-none"
                />
                {detectedNote && <p className="text-body-sm text-muted-foreground">{detectedNote}</p>}
                <div className="flex flex-wrap items-center gap-2 pt-0.5">
                    <PropertyPill
                        icon={<CalendarRange className="size-3.5" />}
                        value={timeframeLabel}
                        options={TIMEFRAME_OPTIONS}
                        customDate
                        onSelect={setTimeframeLabel}
                    />
                    <PropertyPill
                        icon={<Gauge className="size-3.5" />}
                        value={capacityLabel}
                        options={CAPACITY_OPTIONS}
                        units={CAPACITY_UNITS}
                        unitKey={capacityFromLabel(capacityLabel)?.unit}
                        onSelect={setCapacityLabel}
                    />
                </div>
                {overCapacityNote && (
                    <div className="flex gap-2 rounded-gt-md border border-gt-gold/40 bg-gt-gold/10 px-2.5 py-2 text-body-sm">
                        <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-gt-gold" />
                        <div className="min-w-0">
                            <p className="text-secondary-foreground">This is over the weekly capacity you picked.</p>
                            <p className="text-muted-foreground">{overCapacityNote}</p>
                        </div>
                    </div>
                )}
                {showTypePicker && (
                    <div className="flex items-center gap-2 pt-0.5 animate-in fade-in-0 slide-in-from-bottom-1 duration-300">
                        <span className="text-body-sm text-muted-foreground">Goal type</span>
                        <div className="inline-flex rounded-md border border-border p-0.5">
                            {TYPE_OPTIONS.map(({ value, label }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setGoalType(value)}
                                    className={cn(
                                        'rounded-[5px] px-2.5 py-1 text-body-sm transition-[background-color,color,transform] duration-150 active:scale-[0.96]',
                                        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                                        goalType === value
                                            ? 'bg-muted font-medium text-foreground'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {(provenanceNote || onRevise) && (
                    <p className="flex items-center gap-1.5 text-body-sm text-muted-foreground">
                        <Sparkles className="size-3.5 shrink-0 text-primary" />
                        <span>{provenanceNote ?? 'Here’s what we think would work.'}</span>
                    </p>
                )}
                {onRevise && <PlanFeedbackInvite onClick={() => focusPlanFeedback(feedbackRef.current)} />}
                {draft.items.length === 0 && (
                    <p className="text-body-sm text-muted-foreground">
                        Build the plan yourself — add the steps that move this forward.
                    </p>
                )}
            </div>

            {/* Phase timeline — only when the plan ramps. Segments are
                    week-proportional; the plan below stays untouched. */}
            {draft.phases && draft.phases.length > 1 && (
                <div
                    style={{ animationDelay: '40ms', animationFillMode: 'both' }}
                    className="flex gap-1 animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
                >
                    {draft.phases.map((phase) => (
                        <div
                            key={phase.name}
                            style={{ flexGrow: phase.weeks }}
                            className="min-w-0 basis-0 rounded-md bg-muted px-2.5 py-1"
                        >
                            <p className="truncate text-body-sm font-medium leading-snug text-foreground">
                                {phase.name}
                            </p>
                            <p className="truncate text-body-sm leading-snug text-muted-foreground">
                                {phase.cadenceLabel}
                            </p>
                        </div>
                    ))}
                </div>
            )}

            {/* Grouped plan items */}
            <div className="space-y-5">
                {REVIEW_PLAN_GROUPS.map(({ label, addLabel, kind }) => {
                    const groupItems = items.filter((it) => it.kind === kind)
                    const includedCount = groupItems.filter((it) => it.included).length
                    return (
                        <section key={kind} className="space-y-1.5">
                            <div className="flex items-center gap-2 px-1">
                                <span className="text-label-md uppercase tracking-wider text-muted-foreground">
                                    {label}
                                </span>
                                <span className="text-label-md tabular-nums text-muted-foreground/60">
                                    {includedCount}
                                </span>
                            </div>
                            <div className="space-y-0.5">
                                {groupItems.map((item) => {
                                    const delay = rowIndex++ * 80
                                    return (
                                        <div
                                            key={item.id}
                                            style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}
                                            className={cn(
                                                'flex items-center gap-3 rounded-lg px-1 py-1.5',
                                                'animate-in fade-in-0 slide-in-from-bottom-1 duration-300',
                                                'transition-opacity duration-150',
                                                !item.included && 'opacity-50'
                                            )}
                                        >
                                            <Checkbox
                                                checked={item.included}
                                                onCheckedChange={() => toggleItem(item.id)}
                                                aria-label={item.title}
                                            />
                                            {item.kind === 'milestone' && (
                                                <Flag className="size-4 shrink-0 text-gt-gold" />
                                            )}
                                            <input
                                                value={item.title}
                                                onChange={(e) => setItemTitle(item.id, e.target.value)}
                                                placeholder="What needs to happen?"
                                                aria-label={item.title || 'New plan item'}
                                                autoFocus={item.title === ''}
                                                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-body-md touch-field-floor text-foreground placeholder:text-muted-foreground/60 focus-visible:outline-none"
                                            />
                                            <PropertyPill
                                                icon={null}
                                                value={itemLabelForKind(item.kind)}
                                                options={KIND_OPTIONS}
                                                align="end"
                                                onSelect={(next) => setItemKind(item.id, next)}
                                            />
                                            {item.kind === 'cadence' && item.frequencyLabel && (
                                                <PropertyPill
                                                    icon={null}
                                                    value={item.frequencyLabel}
                                                    options={FREQUENCY_OPTIONS}
                                                    align="end"
                                                    onSelect={(freq) => setFrequency(item.id, freq)}
                                                />
                                            )}
                                        </div>
                                    )
                                })}
                                <button
                                    type="button"
                                    onClick={() => addItem(kind)}
                                    className="flex w-full items-center gap-3 rounded-lg px-1 py-1.5 text-left text-body-sm text-muted-foreground/70 transition-colors duration-150 hover:text-foreground"
                                >
                                    <Plus className="size-4 shrink-0" />
                                    <span>{addLabel}</span>
                                </button>
                            </div>
                        </section>
                    )
                })}
            </div>

            {draft.startingPoint && draft.startingPoint.commitments.length > 0 && (
                <section className="space-y-2">
                    <div className="space-y-0.5 px-1">
                        <span className="text-label-md uppercase tracking-wider text-muted-foreground">
                            Built around your week
                        </span>
                        <p className="text-body-sm text-muted-foreground">
                            Selected recurring tasks become goal hooks. Calendar events stay unchanged.
                        </p>
                    </div>
                    <div className="space-y-0.5">
                        {draft.startingPoint.commitments.map((commitment) => {
                            const isReused =
                                commitment.source === 'recurring'
                                    ? selectedRecurringTemplateIds.includes(commitment.id)
                                    : commitment.reused
                            const review = planningCommitmentReview(commitment, 'creation', isReused)
                            return (
                                <div
                                    key={`${commitment.source}-${commitment.id}`}
                                    className="flex flex-wrap items-center gap-2 rounded-lg px-1 py-1.5"
                                >
                                    <span className="shrink-0 text-label-sm text-muted-foreground">
                                        {review.sourceLabel}
                                    </span>
                                    <span
                                        className={cn(
                                            'shrink-0 rounded-sm px-1.5 text-label-sm',
                                            isReused && commitment.source === 'recurring'
                                                ? 'bg-success/15 text-success'
                                                : 'bg-muted text-muted-foreground'
                                        )}
                                    >
                                        {review.inclusionLabel}
                                    </span>
                                    <span className="shrink-0 text-label-sm text-muted-foreground">
                                        {review.relationLabel}
                                    </span>
                                    {review.changeLabel && (
                                        <span className="shrink-0 text-label-sm text-muted-foreground">
                                            {review.changeLabel}
                                        </span>
                                    )}
                                    <span className="min-w-0 flex-1 truncate text-body-sm text-foreground">
                                        {commitment.title}
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                </section>
            )}

            {hookableTemplates.length > 0 && (
                <section className="space-y-1.5">
                    <div className="flex items-center gap-2 px-1">
                        <span className="text-label-md uppercase tracking-wider text-muted-foreground">Goal hooks</span>
                        <span className="text-label-md tabular-nums text-muted-foreground/60">
                            {selectedRecurringTemplateIds.length}
                        </span>
                    </div>
                    <GoalHookCheckboxList
                        items={hookableTemplates}
                        selectedIds={selectedRecurringTemplateIds}
                        onToggle={toggleRecurringTemplate}
                        maxItems={6}
                        showRecurringIcon
                    />
                </section>
            )}
        </>
    )

    const feedback = onRevise && (
        <PlanFeedback ref={feedbackRef} onRevise={(note) => onRevise(note, currentDraft())} isRevising={isRevising} />
    )

    const backButton = onBack && (
        <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground">
            Back
        </Button>
    )
    const confirmLabel = (
        <>
            Create goal
            <kbd className="pointer-events-none inline-flex items-center gap-0.5 rounded bg-primary-foreground/15 px-1.5 py-0.5 text-[11px] font-medium text-primary-foreground/80">
                <span>⌘</span>
                <span>↵</span>
            </kbd>
        </>
    )

    if (layout === 'dialog') {
        return (
            <>
                <GTDialogBody className="gap-5">{body}</GTDialogBody>
                {feedback}
                <GTDialogFooter
                    start={backButton}
                    onConfirm={handleConfirm}
                    confirmDisabled={!canConfirm}
                    confirmLabel={confirmLabel}
                />
            </>
        )
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">{body}</div>
            {feedback && <div className="shrink-0 px-5 pb-3">{feedback}</div>}
            <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-border/70 px-5 py-3">
                <div>{backButton}</div>
                <Button
                    onClick={handleConfirm}
                    disabled={!canConfirm}
                    className="gap-2 transition-transform active:scale-[0.96]"
                >
                    {confirmLabel}
                </Button>
            </footer>
        </div>
    )
}

export default ReviewScreen
